import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  Link as MuiLink,
  Checkbox,
} from '@mui/material';
import { filterProducts, searchProducts } from '../../api/hooks/products';
import ProductFilters from '../../components/ProductFilters';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SearchField from '../../components/SearchField';
import {
  Organization,
  Product,
  ProductVariant,
  ProductWithVariants,
  VariantsWithPrice,
} from 'src/app/api/openapi';
import { GridColDef } from '@mui/x-data-grid-premium';
import CustomGridTreeDataGroupingCell from 'src/app/components/CustomGridTreeDataGroupingCell';
import CustomDataGrid from 'src/app/components/CustomDataGrid';

interface ProductsTableProps {
  toggleSelection?: (row: ProductWithVariants) => void;
  selectedProductsIds?: Record<string, boolean>;
}

const ProductsTable = ({
  toggleSelection,
  selectedProductsIds,
}: ProductsTableProps) => {
  // const { productId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const navigate = useNavigate();

  const handleSearchProducts = (query: string, page = 0) =>
    searchProducts(query, page + 1).then((result) => {
      setProducts(
        result.hits?.reduce(
          (acc: any[], { document }: any) => [
            ...acc,
            { ...document.data, path: [document.id] },
            ...document.data.variants.map((variant: any) => ({
              ...variant,
              path: [document.id, variant.id],
            })),
          ],
          []
        ) || []
      );
      setPaginationModel({ page: result.page - 1, pageSize: 10 });
      setRowCount(result.found);
    });

  useEffect(() => {
    handleSearchProducts('*');
  }, []);

  const onFilter = useCallback(
    (brands: string[], categories: string[]) =>
      filterProducts(brands, categories).then((result) => {
        setProducts(
          result.hits?.reduce(
            (acc: any[], { document }: any) => [
              ...acc,
              { ...document.data, path: [document.id] },
              ...document.data.variants.map((variant: any) => ({
                ...variant,
                path: [document.id, variant.id],
              })),
            ],
            []
          ) || []
        );
        setPaginationModel({ page: result.page - 1, pageSize: 10 });
        setRowCount(result.found);
      }),
    []
  );

  const getVariantName = (variant: ProductVariant) => {
    if (!variant || !variant.varies_by) return '';
    if (variant.title) return variant.title;
    return variant.varies_by.map((variable) => variable.value).join(', ');
  };

  const columns: GridColDef<any>[] = [
    {
      headerName: '',
      field: 'add',
      renderCell: ({ row }) =>
        (row.path.length > 1 && null) || (
          <Checkbox
            checked={
              (selectedProductsIds && selectedProductsIds[row.id]) || false
            }
            onClick={() =>
              toggleSelection && toggleSelection(row as ProductWithVariants)
            }
          />
        ),
      disableColumnMenu: true,
      sortable: false,
      width: 60,
    },
    {
      headerName: 'Name',
      field: 'title',
      renderCell: ({ row }) =>
        row.path.length > 1 ? (
          getVariantName(row as ProductVariant)
        ) : (
          <MuiLink
            component={Link}
            to={`/products/${
              'organization' in row
                ? (row.organization as Organization)?.id
                : 'orgId'
            }/${row.id}`}
            state={row}
          >
            {(row as Product).title || row.id}
          </MuiLink>
        ),
      width: 300,
    },
    {
      headerName: 'Price',
      field: 'suggested_prices',
      renderCell: ({ row }) => {
        if (row.suggested_prices && row.suggested_prices.length)
          return `$${row.suggested_prices[0].amount}`;
        if (row.variants) {
          const minPrice = row.variants.reduce(
            (min: number, variant: any) =>
              variant.suggested_prices &&
              variant.suggested_prices[0] &&
              variant.suggested_prices[0].amount < min
                ? variant.suggested_prices[0].amount
                : min,
            Infinity
          );
          return minPrice !== Infinity ? `Starting at $${minPrice}` : '';
        }
      },
      width: 160,
    },
    {
      headerName: 'Categories',
      field: 'categories',
      renderCell: ({ row }) =>
        (row as ProductWithVariants).google_product_category?.name,
      width: 200,
    },
    {
      headerName: 'GTIN',
      field: 'gtin',
      renderCell: ({ row }) => {
        if (row.variants?.length)
          return (row.variants as VariantsWithPrice[])
            .map((v) => v.gtin)
            .filter((v) => v)
            .join(', ');
        return row.gtin || '';
      },
      width: 200,
    },
  ];

  return (
    <Box>
      {!toggleSelection && (
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h1">Products</Typography>
          <Button variant="flat" onClick={() => navigate('/organizations')}>
            Create Product
          </Button>
        </Stack>
      )}
      <Grid container spacing={2}>
        <Grid item xs={9}>
          <SearchField
            value={searchTerm}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const term = e.target.value;
              setSearchTerm(term);
              handleSearchProducts(term);
            }}
            delay={0}
          />
          <CustomDataGrid
            rows={products}
            columns={columns}
            treeData
            pagination
            paginationMode="server"
            rowCount={rowCount}
            getTreeDataPath={(row) => row.path}
            groupingColDef={{
              headerName: '',
              renderCell: (params) =>
                params.row.variants && params.row.variants.length ? (
                  <CustomGridTreeDataGroupingCell {...params} />
                ) : null,
              width: 55,
              resizable: false,
            }}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) =>
              handleSearchProducts(searchTerm, model.page)
            }
            initialState={{
              columns: {
                columnVisibilityModel: {
                  add: !!toggleSelection,
                },
              },
            }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? 'Mui-even'
                : 'Mui-odd'
            }
          />
        </Grid>
        <Grid item xs={3}>
          <ProductFilters onFilter={onFilter} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductsTable;
